/**
 * Single source of truth for every route in the app.
 *
 * Consumed by:
 *   - App.tsx                  → <Routes> definitions
 *   - scripts/generate-sitemap.js → public/sitemap.xml
 *   - scripts/prerender.js     → the list of URLs to render to static HTML
 *
 * Adding a route means editing this file and nothing else. Keep it free of
 * JSX and React imports so the plain-Node build scripts can require it.
 */

export type RouteEntry = {
	path: string;
	/** Public routes go in the sitemap and are prerendered for crawlers. */
	indexable: boolean;
	/** Sitemap <lastmod>. Bump when the page's content meaningfully changes. */
	lastmod?: string;
};

export const SITE_URL = 'https://phasmasolutions.com';

/** Public project pages. `path` must match the entries in ProjectsDescription.tsx. */
export const PROJECT_ROUTES: RouteEntry[] = [
	{ path: '/AStar-Visual-Representation', indexable: true, lastmod: '2024-07-01' },
	{ path: '/Calculator-Ip', indexable: true, lastmod: '2024-07-01' },
	{ path: '/AutoClicker', indexable: true, lastmod: '2024-07-01' },
	{ path: '/Weatheron', indexable: true, lastmod: '2024-07-01' },
	{ path: '/Discord-Bot', indexable: true, lastmod: '2024-07-01' },
	{ path: '/Exotic-Island', indexable: true, lastmod: '2024-07-01' },
	{ path: '/Perlin-Noise-Visual-Representation', indexable: true, lastmod: '2024-07-01' },
	{ path: '/Google-Forms-Copy', indexable: true, lastmod: '2024-07-01' },
	{ path: '/Ultimate-Shooter', indexable: true, lastmod: '2024-07-01' },
	{ path: '/Finelog-App', indexable: true, lastmod: '2025-05-20' },
	{ path: '/Project-Penguins', indexable: true, lastmod: '2024-07-01' },
	{ path: '/Crypto-Transactions-Visualizer', indexable: true, lastmod: '2025-05-20' },
	{ path: '/SHA-256', indexable: true, lastmod: '2024-07-01' },
	{ path: '/ONP-Calculator', indexable: true, lastmod: '2025-06-17' },
];

/**
 * Private / personal routes. Never indexed, never prerendered, and listed in
 * robots.txt Disallow. They also pass noIndex to <Seo>.
 */
export const PRIVATE_ROUTES: RouteEntry[] = [
	{ path: '/invitation-julcia', indexable: false },
	{ path: '/christmas-2025', indexable: false },
	{ path: '/christmas-2025/content/babcia-dziadek', indexable: false },
	{ path: '/christmas-2025/content/tata', indexable: false },
	{ path: '/christmas-2025/content/laura-iwo', indexable: false },
	{
		path: '/d9b5989ea1a82edfd722cd5ffe6ba6d026ff00b369bf8ff7f332165a717c07de',
		indexable: false,
	},
];

export const HOME_ROUTE: RouteEntry = {
	path: '/',
	indexable: true,
	lastmod: '2026-07-12',
};

export const ALL_ROUTES: RouteEntry[] = [
	HOME_ROUTE,
	...PROJECT_ROUTES,
	...PRIVATE_ROUTES,
];

/** Routes that belong in sitemap.xml and get prerendered to static HTML. */
export const INDEXABLE_ROUTES: RouteEntry[] = ALL_ROUTES.filter(
	(r) => r.indexable
);
